import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../app/components/AppButton';
import { formatMajorAmount } from '../../application/formatLessonMoney';
import type { LessonOffering, LessonPackageDraft } from '../../application/types';
import {
  useCreateLessonPackageMutation,
  useUpdateLessonPackageMutation,
} from '../../infrastructure/api/lessonOfferings.api';
import { LessonPackageForm, packageDraftQuote } from './LessonPackageForm';

const emptyDraft: LessonPackageDraft = { quantity: '5', discountPercent: '5' };

type Props = {
  offering: LessonOffering;
  initialOpen?: boolean;
};

export function LessonPackageSection({ offering, initialOpen = false }: Props) {
  const { t, i18n } = useTranslation();
  const [createPackage, { isLoading: isCreating }] = useCreateLessonPackageMutation();
  const [updatePackage, { isLoading: isUpdating }] = useUpdateLessonPackageMutation();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<LessonPackageDraft>(emptyDraft);
  const [isOpen, setIsOpen] = useState(initialOpen);
  const isSaving = isCreating || isUpdating;
  const quote = packageDraftQuote(offering.price, draft);

  const openCreate = () => {
    setEditingId(null);
    setDraft(emptyDraft);
    setIsOpen(true);
  };

  const openEdit = (packageId: number) => {
    const current = offering.packages.find((pkg) => pkg.id === packageId);
    if (!current) return;
    setEditingId(current.id);
    setDraft({
      quantity: String(current.quantity),
      discountPercent: String(current.discountPercent),
    });
    setIsOpen(true);
  };

  const onSave = async () => {
    if (!quote) return;
    const quantity = Number(draft.quantity);
    const discountPercent = Number(draft.discountPercent);
    try {
      if (editingId == null) {
        await createPackage({
          offeringId: offering.id,
          quantity,
          discountPercent,
        }).unwrap();
      } else {
        await updatePackage({ id: editingId, quantity, discountPercent }).unwrap();
      }
      setIsOpen(false);
    } catch {
      // The mutation shows the error toast.
    }
  };

  return (
    <section className="mentor-lessons__packages">
      <h2>{t('lesson.packages')}</h2>
      {offering.packages.length === 0 ? (
        <p className="mentor-lessons__muted">{t('lesson.packages_empty')}</p>
      ) : (
        <ul className="mentor-lessons__package-list">
          {offering.packages.map((pkg) => (
            <li key={pkg.id}>
              <span>
                {t('lesson.package_line', {
                  quantity: pkg.quantity,
                  price: formatMajorAmount(pkg.price, pkg.currency, i18n.language, 2),
                  percent: pkg.discountPercent,
                })}
              </span>
              <button type="button" onClick={() => openEdit(pkg.id)}>
                {t('lesson.edit')}
              </button>
            </li>
          ))}
        </ul>
      )}
      {isOpen ? (
        <>
          <LessonPackageForm
            offeringPrice={offering.price}
            currency={offering.currency}
            value={draft}
            onChange={setDraft}
          />
          <div className="mentor-lessons__actions">
            <Button type="button" onClick={() => setIsOpen(false)} disabled={isSaving}>
              {t('lesson.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={onSave}
              disabled={!quote || isSaving}
            >
              {editingId == null ? t('lesson.create_package') : t('lesson.save')}
            </Button>
          </div>
        </>
      ) : (
        <Button type="button" variant="primary" onClick={openCreate}>
          {t('lesson.add_package')}
        </Button>
      )}
    </section>
  );
}
